import { useCallback } from "react"
import { useNavigate, useParams } from "react-router"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { ConfirmLayout } from "@/templates/ConfirmLayout"
import { assertIsDefined } from "@/utils/assetIsDefined"
import { genCleaningReportPagePath } from "@/routes/protected"
import { callRequestReport } from "@/hooks/useCleaningInfos"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import { BackToDetailConfirmDialog } from "@/components/Dialog/BackToDetailConfirmDialog"
import { useConfirmDialog } from "@/hooks/useConfirmDialog"
import { useNavigateBackToDetail } from "@/hooks/useNavigateBackToDetail"
import { useTranslation } from "@/hooks/useTranslation"

interface TroublesConfirmPageProps {
    title: string
}

export const TroublesConfirmPage = ({ title }: TroublesConfirmPageProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { cleaningId } = useParams<"cleaningId">()
    assertIsDefined(cleaningId)

    const { token } = useAuth()

    // 清掃詳細画面に戻る確認モーダル
    const {
        confirmBackOpen,
        openConfirmBack,
        closeConfirmBack,
        goBackToDetail
    } = useNavigateBackToDetail("cleaning", cleaningId)

    // 報告終了の確認モーダル
    const {
        open: confirmFinishOpen,
        openDialog: openConfirmFinish,
        closeDialog: closeConfirmFinish
    } = useConfirmDialog()

    const gotoTroubles = useCallback(() => {
        navigate(genCleaningReportPagePath(cleaningId, "troubles"))
    }, [cleaningId, navigate])

    const finishAndGotoSchedule = useCallback(async () => {
        closeConfirmFinish()
        await callRequestReport(token, cleaningId)
        navigate(genCleaningReportPagePath(cleaningId, "finish"))
    }, [cleaningId, navigate, token, closeConfirmFinish])

    return (
        <>
            <ConfirmLayout
                headerTitle={t("トラブル報告")}
                onNext={openConfirmBack}
                title={title}
                primaryButtonLabel={t("トラブルを報告する")}
                cancelButtonLabel={t("清掃報告終了")}
                next={gotoTroubles}
                cancel={openConfirmFinish}
            />
            {/* 報告終了の確認モーダル */}
            <ConfirmDialog
                open={confirmFinishOpen}
                title={t("清掃報告を終了する")}
                message={t(
                    "清掃報告を終了すると、清掃詳細画面に戻れなくなるため、鍵情報を確認することができなくなります。"
                )}
                onConfirm={finishAndGotoSchedule}
                onCancel={closeConfirmFinish}
                cancelLabel={t("戻る")}
                confirmLabel={t("終了")}
            />
            {/* 清掃詳細画面に戻る確認モーダル */}
            <BackToDetailConfirmDialog
                type="cleaning"
                open={confirmBackOpen}
                onClose={closeConfirmBack}
                onConfirm={goBackToDetail}
            />
        </>
    )
}
